import React from "react";
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";

import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper";
import { motion } from "framer-motion";

import {
  fetchRoomAllocations,
  fetchSelectedRoomData,
  fetchTroubleReports,
} from "./../BackendHelpers";

import RoomDetails from "./slides/landingpage/Landingpage";
import CapacityAccessability from "./slides/capacity-accessability/CapacityAccessability";
import EquipmentHardware from "./slides/equipment-hardware/EquipmentHardware";
import Allocations from "./slides/allocations/Allocations";
import RoomTroubleReports from "./slides/room-trouble-reports/RoomTroubleReports";
import Gallery from "./slides/gallery/Gallery";
import CommonPois from "./slides/pois/CommonPois";
import SmartTabs from "./../tabs/SmartTabs";

import "./RoomStyle.css";

const Room = ({ serverData, loggedIn, setLoggedIn }) => {
  const [roomDetailsSwiper, setRoomDetailsSwiper] = useState({});
  const [selectedRoom, setSelectedRoom] = useState();
  const [troubleReports, setTroubleReports] = useState([]);
  const [allAllocations, setAllAllocations] = useState([]);
  const { roomID } = useParams();

  /* React hook that will be executed on creation and every time the dependencies update. */
  // check if user is authed and also after a login via mask
  useEffect(() => {
    const getRoomData = async () => {
      /*
       * Room Data
       */
      const roomData = await fetchSelectedRoomData(roomID);
      setSelectedRoom(roomData);

      /*
       * Trouble Reports
       */
      if (roomData.newStandardId) {
        const troubleReportsFromServer = await fetchTroubleReports(
          roomData.newStandardId,
        );

        /**
         * Method to map the trouble reports to a more declarative and minimalized dataset
         */
        const troubleReportsSortedFiltered = troubleReportsFromServer.map(
          (item, domID) => ({
            headline: item.Auftragsart,
            content: item.AFBeschreibung,
            creationDate: item.AufgetretenAm,
            id: domID++,
          }),
        );
        setTroubleReports(troubleReportsSortedFiltered);
      } else {
        setTroubleReports([]);
      }

      /*
       * Room Allocations
       */
      const allAllocationsFromServer = await fetchRoomAllocations(roomData.id);
      /**
       * Filter() and Map() the allocations data to only display relevant information.
       * Basic metaphor: extract relevent parts from the iCal dataset.
       */
      const allAllocationsFromServerFiltered = Object.values(
        allAllocationsFromServer,
      )
        .filter((item) => {
          if (item.type === "VEVENT") return true;
          else return false;
        })
        .map((item) => ({
          id: item.uid,
          title: item.categories.toString(),
          start: item.start.toString(),
          end: item.end.toString(),
        }));
      setAllAllocations(allAllocationsFromServerFiltered);
    };

    getRoomData();
  }, [roomID, loggedIn]);

  return (
    <>
      <motion.div
        className="room-details"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0, transition: { duration: 0.3 } }}
      >
        {/* Root swiper component for the detials page */}
        <Swiper
          autoHeight={true}
          spaceBetween={24}
          pagination={{
            clickable: true,
            dynamicBullets: true,
            horizontalClass: "detailsSwiper-pagination",
          }}
          modules={[Pagination]}
          className="detailsSwiper"
          onInit={(sc) => {
            setRoomDetailsSwiper(sc);
          }}
        >
          <SwiperSlide>
            <RoomDetails
              selectedRoom={selectedRoom ? selectedRoom : ""}
              swiperComponent={roomDetailsSwiper}
              troubleReportData={troubleReports}
            />
          </SwiperSlide>

          <SwiperSlide className="pois">
            <CommonPois selectedRoom={selectedRoom ? selectedRoom : ""} />
          </SwiperSlide>

          <SwiperSlide>
            <CapacityAccessability
              selectedRoom={selectedRoom ? selectedRoom : ""}
              loggedIn={loggedIn}
              loginSetter={setLoggedIn}
            />
          </SwiperSlide>

          <SwiperSlide>
            <EquipmentHardware
              selectedRoom={selectedRoom ? selectedRoom : ""}
              loggedIn={loggedIn}
              loginSetter={setLoggedIn}
            />
          </SwiperSlide>

          <SwiperSlide>
            <Allocations
              selectedRoom={selectedRoom ? selectedRoom : ""}
              loggedIn={loggedIn}
              loginSetter={setLoggedIn}
              allocations={allAllocations}
            />
          </SwiperSlide>

          <SwiperSlide>
            <RoomTroubleReports
              selectedRoom={selectedRoom ? selectedRoom : ""}
              troubleReportData={troubleReports}
            />
          </SwiperSlide>

          <SwiperSlide className="no-vertical-overflow gallery">
            <Gallery selectedRoom={selectedRoom ? selectedRoom : ""} />
          </SwiperSlide>
        </Swiper>
      </motion.div>

      <SmartTabs
        allRooms={serverData.allRooms ? serverData.allRooms : []}
        troubleReportData={
          [] /* function disabled - mainable reasons to allow future re-enable */
        }
      />
    </>
  );
};

export default Room;
